'use client'
import { PaymentType } from '@repo/shared/components'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { useInitiateWithdrawFiat } from '@repo/services'
import {
  Alert,
  Button,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  Row,
  Typography,
} from '@repo/ui/components'

import { useState } from 'react'
import { Card } from './withdraw-online-payment.container'
import { CardsItem } from '@repo/profile/containers'
import { useRouter } from '@repo/shared/i18n/navigation'

interface WithdrawOnlinePaymentDialogProps {
  children: React.ReactNode
  card: Card | undefined
  amount: number
  fee: number
  isValidate: boolean
}

export const WithdrawOnlinePaymentDialog = (
  props: WithdrawOnlinePaymentDialogProps,
) => {
  const { children, card, amount, fee, isValidate } = props
  const { t } = useTranslate()
  const [open, setOpen] = useState(false)
  const { push } = useRouter()
  const { errorHandler } = useErrorHandler()

  const initiateWithdrawFiat = useInitiateWithdrawFiat({ invalidate: true })
  const onConfirm = () => {
    initiateWithdrawFiat.mutate(
      {
        amount: amount,
        currency: 'IRR',
        reference: `withdraw-${amount}-${card?.id}`,
        bankID: card?.id,
      },
      {
        onSuccess: () => {
          push(`/history/balance`)
        },
        onError: errorHandler,
      },
    )
  }

  const handleOpenChange = (open: boolean) => {
    if (isValidate) {
      setOpen(open)
    }
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalTrigger asChild>{children}</ModalTrigger>
      <ModalContent className='sm:max-w-[425px]'>
        <ModalHeader>
          <ModalTitle>
            {t('withdrawOnlinePaymentPage_request_title')}
          </ModalTitle>
        </ModalHeader>

        <Row className='justify-between'>
          <Typography
            className='flex-1'
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {t('withdrawOnlinePaymentPage_request_destination')}
          </Typography>
          {card && (
            <CardsItem
              showBankName={false}
              sheba={card.sheba ?? ''}
              enName={card.enName ?? ''}
            />
          )}
        </Row>
        <Row className='justify-between'>
          <Typography
            className='flex-1'
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {t('withdrawOnlinePaymentPage_request_amount')}
          </Typography>
          <PaymentType value={amount} />
        </Row>
        <Row className='justify-between'>
          <Typography
            className='flex-1'
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {t('withdrawOnlinePaymentPage_request_fee')}
          </Typography>
          <PaymentType value={fee} />
        </Row>
        <Row className='justify-between'>
          <Typography
            className='flex-1'
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {t('withdrawOnlinePaymentPage_request_total')}
          </Typography>
          <PaymentType value={amount - fee} />
        </Row>
        <Row className='justify-between'>
          <Typography
            className='flex-1'
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {t('withdrawOnlinePaymentPage_request_estimated_time')}
          </Typography>
          <Typography className='text-neutral-surface-80' variant='bdi'>
            امروز ۸:۴۵ تا فردا ۱۰:۰۰
          </Typography>
        </Row>
        <Alert
          color='base'
          size='md'
          caption={t('withdrawOnlinePaymentPage_request_estimated_time_hint')}
          icon={<Icon name='AlertTriangle' size='sm' />}
        />
        <ModalFooter>
          <Button
            variant='fill'
            color='base'
            width='full'
            size='md'
            onClick={onConfirm}
            loading={initiateWithdrawFiat.isPending}
          >
            {t('withdrawOnlinePaymentPage_withdraw_confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
